import { useMemo } from 'react';

import {
  EsignCrmLeadFieldsFragment,
  EsignCrmLeadStatusesQuery,
  EsignDraftDefaultsQuery,
  EsignPaymentPlansQuery,
  useEsignCrmLeadLabelQuery,
  useEsignCrmLeadsQuery,
  useEsignCrmLeadStatusesQuery,
  useEsignDraftDefaultsQuery,
  useEsignGroupLabelQuery,
  useEsignGroupOptionsQuery,
  useAllTeachersQuery,
  useEsignPaymentPlansQuery,
  useEsignPersonOptionsQuery,
} from 'core/graphql';

import { useDebouncedValue } from '../Meta/useDebouncedValue';
import { useEsignPermissions } from './useEsignPermissions';

export type IEsignPaymentPlan = EsignPaymentPlansQuery['paymentPlans'][number];

export type IEsignDraftDefaults = NonNullable<
  EsignDraftDefaultsQuery['contractDraftDefaults']
>;

export interface IEsignGroupOption {
  id: string;
  name: string;
  courseName: string;
  startDate: string | null;
  currency: string | null;
}

/** ТЗ 0079: лид воронки New Leads в Kommo. */
export type IEsignCrmLead = EsignCrmLeadFieldsFragment;

export type IEsignCrmLeadStatus = NonNullable<
  EsignCrmLeadStatusesQuery['crmLeadStatuses']
>['items'][number];

export interface IEsignCrmError {
  code: string;
  message: string;
}

export interface IEsignPersonOption {
  userId: string;
  studentId: string;
  name: string;
  email: string;
  phone: string;
}

const EMPTY_PLANS: IEsignPaymentPlan[] = [];

/** Справочник тарифов. Только для тех, кто создаёт студенческий договор. */
export const useEsignPaymentPlans = (enabled = true) => {
  const { canReadStudent } = useEsignPermissions();
  const { data, loading, error } = useEsignPaymentPlansQuery({
    skip: !enabled || !canReadStudent,
    fetchPolicy: 'cache-first',
  });
  return {
    plans: data?.paymentPlans ?? EMPTY_PLANS,
    loading,
    error,
  };
};

interface IDraftDefaultsArgs {
  groupId: string | null;
  paymentPlanId: string | null;
  currency: string;
  email: string;
  phone: string;
  excludeContractId?: string | null;
  /** ТЗ 0079: предупредить, если по лиду уже есть договор. */
  crmLeadId?: string | null;
}

/**
 * Подсказки бэкенда для формы «Новый договор»: прайс, график, номер,
 * найденный человек и предупреждения.
 *
 * Email и телефон уходят с задержкой: без неё каждая набранная буква была бы
 * запросом с поиском человека по всей базе. Пока результат для новых
 * переменных не пришёл, форма держит прошлый ответ (`previousData`) — иначе
 * прайс и номер мигали бы на каждое нажатие.
 */
export const useEsignDraftDefaults = ({
  groupId,
  paymentPlanId,
  currency,
  email,
  phone,
  excludeContractId,
  crmLeadId,
}: IDraftDefaultsArgs) => {
  const { canWriteStudent } = useEsignPermissions();
  const debouncedEmail = useDebouncedValue(email.trim(), 500);
  const debouncedPhone = useDebouncedValue(phone.trim(), 500);
  const skip = !canWriteStudent || !groupId || !paymentPlanId;

  const { data, previousData, loading, error } = useEsignDraftDefaultsQuery({
    variables: {
      groupId: groupId || '',
      paymentPlanId: paymentPlanId || '',
      currency: currency || null,
      email: debouncedEmail || null,
      phone: debouncedPhone || null,
      // Правка черновика: сам черновик бэкенд не считает дублем.
      excludeContractId: excludeContractId || null,
      crmLeadId: crmLeadId || null,
    },
    skip,
    fetchPolicy: 'network-only',
  });

  const defaults = skip
    ? null
    : (data ?? previousData)?.contractDraftDefaults ?? null;

  return { defaults, loading, error };
};

/**
 * Поиск группы по названию на сервере + подпись уже выбранной группы (она
 * может не попасть в первые 20 результатов пустого поиска).
 */
export const useEsignGroupOptions = (
  search: string,
  selectedId: string | null,
  enabled = true,
) => {
  const debounced = useDebouncedValue(search.trim(), 300);
  const { data, loading } = useEsignGroupOptionsQuery({
    variables: { query: debounced, first: 20 },
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
  });
  const { data: labelData } = useEsignGroupLabelQuery({
    variables: { groupId: selectedId },
    skip: !enabled || !selectedId,
    fetchPolicy: 'cache-first',
  });

  const options = useMemo<IEsignGroupOption[]>(() => {
    const found = (data?.allGroups?.edges ?? [])
      .map((edge) => edge?.node)
      .filter((node): node is NonNullable<typeof node> => Boolean(node))
      .map((node) => ({
        id: node.id,
        name: node.name,
        courseName: node.course?.name ?? '',
        startDate: node.startDate ?? null,
        currency: node.currency ?? null,
      }));
    const selected = labelData?.group;
    if (selected && !found.some((option) => option.id === selected.id)) {
      found.unshift({
        id: selected.id,
        name: selected.name,
        courseName: selected.course?.name ?? '',
        startDate: selected.startDate ?? null,
        currency: selected.currency ?? null,
      });
    }
    return found;
  }, [data, labelData]);

  return { options, loading };
};

/**
 * Поиск человека для «Повторить зачисление» (`ambiguous_person`).
 */
export const useEsignPersonOptions = (search: string, enabled = true) => {
  const debounced = useDebouncedValue(search.trim(), 300);
  const { data, loading } = useEsignPersonOptionsQuery({
    variables: { query: debounced, first: 20 },
    skip: !enabled || debounced.length < 2,
    fetchPolicy: 'network-only',
  });

  const options = useMemo<IEsignPersonOption[]>(
    () =>
      (data?.allStudents?.edges ?? [])
        .map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node?.user))
        .map((node) => ({
          userId: node.user?.id ?? '',
          studentId: node.id,
          name:
            [node.user?.firstName, node.user?.lastName]
              .filter(Boolean)
              .join(' ') || '—',
          email: node.user?.email ?? '',
          phone: node.user?.phone ?? '',
        })),
    [data],
  );

  return { options, loading };
};

export interface IEsignTeacherOption {
  id: string;
  name: string;
  email: string;
}

const EMPTY_TEACHERS: IEsignTeacherOption[] = [];

/**
 * Поиск преподавателя для окна «Договор с преподавателем» — тот же
 * `allTeachers(query:)`, что у палитры команд и списка преподавателей (имя,
 * фамилия, email). Пустой поиск — первые 20 по порядку сервера.
 *
 * 🔴 `no-cache`, как у палитры: у `allTeachers` в кэше
 * `relayStylePagination()` без keyArgs, и выдача поиска склеилась бы со
 * списком на странице «Преподаватели».
 *
 * Права на запрос — те же роли, что у документов преподавателей
 * (`resolve_all_teachers`: Admin, AM, Sales, HR), поэтому гейт — тот же
 * `canTeacherDocs`.
 */
export const useEsignTeacherOptions = (search: string, enabled = true) => {
  const { canTeacherDocs } = useEsignPermissions();
  const debounced = useDebouncedValue(search.trim(), 300);
  const { data, previousData, loading } = useAllTeachersQuery({
    variables: { query: debounced || null, first: 20 },
    skip: !enabled || !canTeacherDocs,
    fetchPolicy: 'no-cache',
  });

  const edges = (data ?? previousData)?.allTeachers?.edges;
  const options = useMemo<IEsignTeacherOption[]>(
    () =>
      edges
        ? edges
            .map((edge) => edge?.node)
            .filter((node): node is NonNullable<typeof node> => Boolean(node))
            .map((node) => ({
              id: node.id,
              name: node.fullName || '—',
              email: node.user?.email ?? '',
            }))
        : EMPTY_TEACHERS,
    [edges],
  );

  return { options, loading };
};

const EMPTY_LEADS: IEsignCrmLead[] = [];
const EMPTY_STATUSES: IEsignCrmLeadStatus[] = [];

/**
 * ТЗ 0079: лиды New Leads для выбора подписанта. Пустой поиск — свежие
 * изменённые; набранный текст ищет сервер (в Kommo), с задержкой.
 *
 * Выбранный лид — отдельным запросом: у черновика он может быть давно не на
 * первой странице. Kommo недоступен — `error`, форма работает руками.
 */
export const useEsignCrmLeadOptions = (
  search: string,
  statusId: string | null,
  selectedId: string | null,
  enabled = true,
) => {
  const debounced = useDebouncedValue(search.trim(), 300);
  const { data, previousData, loading } = useEsignCrmLeadsQuery({
    variables: { query: debounced || null, statusId: statusId || null },
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
  });
  const { data: labelData } = useEsignCrmLeadLabelQuery({
    variables: { id: selectedId || '' },
    skip: !enabled || !selectedId,
    fetchPolicy: 'cache-first',
  });

  const page = (data ?? previousData)?.crmLeads;
  const options = useMemo<IEsignCrmLead[]>(() => {
    const found = page?.items ?? EMPTY_LEADS;
    const selected = labelData?.crmLead;
    if (selected && !found.some((lead) => lead.id === selected.id)) {
      return [selected, ...found];
    }
    return found;
  }, [page, labelData]);

  const error: IEsignCrmError | null = page?.error ?? null;
  return {
    options,
    selected: labelData?.crmLead ?? null,
    hasMore: Boolean(page?.hasMore),
    error,
    loading,
  };
};

/** ТЗ 0079: этапы воронки New Leads — фильтр списка лидов. */
export const useEsignCrmLeadStatuses = (enabled = true) => {
  const { data, loading } = useEsignCrmLeadStatusesQuery({
    skip: !enabled,
    fetchPolicy: 'cache-first',
  });
  return {
    statuses: data?.crmLeadStatuses.items ?? EMPTY_STATUSES,
    error: data?.crmLeadStatuses.error ?? null,
    loading,
  };
};
